import { format, parseISO } from "date-fns";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Sparkle, User as UserIcon, FlaskConical } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { BrandSignal } from "@/data/mockBrandSignalsData";
import { ConfidenceBars, CONFIDENCE_LABELS } from "./ConfidenceBars";
import { STATUS_BG, STATUS_LABELS } from "./signalTableConstants";

interface SignalTableRowProps {
  signal: BrandSignal;
  onClick: (signal: BrandSignal) => void;
}

export function SignalTableRow({ signal: s, onClick }: SignalTableRowProps) {
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40 transition-colors border-b border-border/50"
      onClick={() => onClick(s)}
    >
      <TableCell className="py-2 align-top w-[380px] min-w-[380px]">
        <p className="leading-snug whitespace-normal break-words text-xs">{s.hypothesis}</p>
      </TableCell>
      <TableCell className="py-2 whitespace-nowrap text-xs">{s.category}</TableCell>
      <TableCell className="py-2">
        <Badge
          variant="secondary"
          className={`border-0 text-foreground text-xs capitalize ${STATUS_BG[s.status]}`}
        >
          {STATUS_LABELS[s.status]}
        </Badge>
      </TableCell>
      <TableCell className="py-2">
        <div className="inline-flex items-center gap-2">
          <ConfidenceBars confidence={s.confidence} />
          <span className="text-xs capitalize">{CONFIDENCE_LABELS[s.confidence]}</span>
        </div>
      </TableCell>
      <TableCell className="py-2 tabular-nums text-center">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1">
                <FlaskConical className="h-3 w-3" />
                <span className="text-trend-positive">{s.supportingTests}</span>
                {s.refutingTests > 0 && (
                  <>
                    <span className="text-muted-foreground">/</span>
                    <span className="text-trend-negative">{s.refutingTests}</span>
                  </>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {s.supportingTests} supporting · {s.refutingTests} refuting
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="py-2 text-center">
        {s.source === "ai" ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger>
                <div
                  className="inline-flex h-5 w-5 rounded items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #342799, #A242A2, #FF534C)" }}
                >
                  <Sparkle className="h-3 w-3 text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent>Signal AI</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger>
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-muted">
                  <UserIcon className="h-2.5 w-2.5 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Manual</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </TableCell>
      <TableCell className="py-2 tabular-nums text-right whitespace-nowrap text-xs">
        {format(parseISO(s.lastUpdated), "MMM d")}
      </TableCell>
    </TableRow>
  );
}
